import React, { useState, useCallback, useEffect } from "react";
import { Canvas } from "@react-three/fiber";

import { useLayerStore } from "../../store/useLayerStore.js";
import { useProgressStore } from "../../store/progressStore.js";
import { useGeometrySelectionStore } from "../../store/useGeometrySelectionStore.js";
import { Stage } from "../../constants/progress.js";

import Map3DPlane from "./Map3DPlane";
import TopographyComponent from "../../features/Topography/TopographyComponent.jsx";
import FlurstuckGeometry from "../../features/sites/Sitecomponent";
import BuildingGeometry from "../../features/Buildings/Buildingcomponent.jsx";
import RiskAreaGeometry from "../../features/sites/RiskAreacomponent.jsx";
import BPLanGeometry from "../../features/sites/BPlancomponent.jsx";
import LanduseGeometry from "../../features/sites/Landusecomponent.jsx";
import NotificationBanner from "../UserInterface/Notificationbar.jsx";
import ShareButton from "../Buttons/ShareButton.jsx";

const LOADING_STAGES = [
  Stage.IDLE,
  Stage.QUERY,
  Stage.QUERYSTAGE,
  Stage.PROCESSING,
  Stage.PROCESSINGSTAGE,
  Stage.CREATE,
  Stage.LOADING,
];


const useGeometryNotification = (
  key,
  stage,
  visibility,
  loadingMsg,
  errorMsg,
  timedoutMsg,
  setNotification,
  clearNotification
) => {
  useEffect(() => {
    if (!visibility) {
      clearNotification(key);
      return;
    }
    if (stage === Stage.NULL || stage === Stage.ERROR) {
      setNotification(key, "error", errorMsg);
    } else if (stage === Stage.TIMEDOUT) {
      setNotification(key, "error", timedoutMsg);
    } else if (LOADING_STAGES.includes(stage)) {
      setNotification(key, "info", loadingMsg);
    } else if (stage === Stage.DONE) {
      clearNotification(key);
    }
  }, [stage, visibility]); 
};


const ThreeDView = () => {
  
  const [mapImage] = useState(() => {
    
    return window.__mapImage ?? null;
  });

  // Layer visibility
  const visibleLayers         = useLayerStore((state) => state.visibleLayers);
  const buildingVisibility    = visibleLayers.gebaude;
  const topographyVisibility  = visibleLayers.topographie;
  const flurstuckeVisibility  = visibleLayers.flurstucke;
  const riskareaVisibility    = visibleLayers.riskGebiet;
  const bauleitplaneVisibility = visibleLayers.bauleitplane;

  // Progress stages
  const buildingsstage  = useProgressStore((state) => state.buildings.stage);
  const sitePlotsstage  = useProgressStore((state) => state.sitePlots.stage);
  const bplanStage      = useProgressStore((state) => state.bplan.stage);
  const topographyStage = useProgressStore((state) => state.topography.stage);
  const riskareaStage   = useProgressStore((state) => state.riskarea.stage);

  
  const [notifications, setNotifications] = useState({});

  const setNotification = useCallback((key, variant, message) => {
    setNotifications((prev) => ({ ...prev, [key]: { variant, message } }));
  }, []);

  const clearNotification = useCallback((key) => {
    setNotifications((prev) => ({ ...prev, [key]: null }));
  }, []);

  const { clearSelection } = useGeometrySelectionStore();

 
  useGeometryNotification(
    "buildings", buildingsstage, buildingVisibility,
    "Gebäudedaten werden geladen...",
    "Gebäudedaten sind derzeit nicht verfügbar.",
    "Gebäudedaten: Die Anfrage hat länger als erwartet gedauert und ist abgelaufen. Bitte versuch es noch einmal.",
    setNotification, clearNotification
  );
  useGeometryNotification(
    "sitePlots", sitePlotsstage, flurstuckeVisibility,
    "Flurstücksdaten werden geladen...",
    "Flurstücksdaten sind derzeit nicht verfügbar.",
    "Flurstücksdaten: Die Anfrage hat länger als erwartet gedauert und ist abgelaufen. Bitte versuch es noch einmal.",
    setNotification, clearNotification
  );
  useGeometryNotification(
    "topography", topographyStage, topographyVisibility,
    "Topographiedaten werden geladen...",
    "Topographiedaten sind derzeit nicht verfügbar.",
    "Topographiedaten: Die Anfrage hat länger als erwartet gedauert und ist abgelaufen. Bitte versuch es noch einmal.",
    setNotification, clearNotification
  );
  useGeometryNotification(
    "bplan", bplanStage, bauleitplaneVisibility,
    "Bebauungsplan wird geladen...",
    "Bebauungsplandaten sind derzeit nicht verfügbar.",
    "Bebauungsplandaten: Die Anfrage hat länger als erwartet gedauert und ist abgelaufen. Bitte versuch es noch einmal.",
    setNotification, clearNotification
  );
  useGeometryNotification(
    "riskarea", riskareaStage, riskareaVisibility,
    "Risikogebiete werden geladen...",
    "Risikogebietsdaten sind derzeit nicht verfügbar.",
    "Risikogebietsdaten: Die Anfrage hat länger als erwartet gedauert und ist abgelaufen. Bitte versuch es noch einmal.",
    setNotification, clearNotification
  );

  const handleBackgroundClick = useCallback(() => {
    clearSelection();
  }, [clearSelection]);

  return (
    <>
      
      <div
        style={{
          position:       "fixed",
          inset:          0,
          display:        "flex",
          flexDirection:  "column",
          justifyContent: "flex-end",
          alignItems:     "center",
          paddingBottom:  "24px",
          gap:            "8px",
          pointerEvents:  "none",
          zIndex:         100,
        }}
      >
        {Object.entries(notifications).map(([key, notif]) =>
          notif ? (
            <div key={key}>
              <NotificationBanner
                message={notif.message}
                variant={notif.variant}
                dismissible={notif.variant !== "info"}
              />
            </div>
          ) : null
        )}
      </div>

      <ShareButton />

      <Canvas
        style={{
          position: "absolute",
          top:      0,
          left:     0,
          width:    "100%",
          height:   "100%",
        }}
        camera={{ position: [0, 0, 250], fov: 50, far: 5000, near: 0.1 }}
        gl={{ antialias: true, preserveDrawingBuffer: true }}
        onPointerMissed={handleBackgroundClick}
      >
        <Map3DPlane mapImage={mapImage} />
        <TopographyComponent />
        <FlurstuckGeometry />
        <BuildingGeometry />
        <RiskAreaGeometry />
        <BPLanGeometry />
        <LanduseGeometry />
      </Canvas>
    </>
  );
};

export default ThreeDView;